import { useEffect, useRef, useState } from 'react';
import { Canvas, Polyline, type FabricObject } from 'fabric';
import { useCanvasEditor } from './CanvasEditorContext';
import ContextMenu, { type ContextMenuItem } from './ContextMenu';
import CanvasFloatingToolbar from './CanvasFloatingToolbar';
import { playObjectAnimation, stopObjectAnimation, type AnimatableObject } from './CanvasAnimations';
import {
  LAYER_NAME_DRAG_TYPE,
  LAYER_SOURCE_DRAG_TYPE,
  alignLayer,
  enforceSelectionLocks,
  getLayerName,
  isTargetLocked,
  isTemplateLayer,
  keepGuidesOnTop,
  moveLayer,
  parseLayerSourceRef,
  setTargetLocked,
} from './CanvasLayers';
import { CANVAS_CUSTOM_PROPS } from './CanvasHistory';

const FIT_PADDING = 40; // Leaves breathing room between the canvas and the wrapper edge.
const ZOOM_STEP = 0.1;
const CLONE_OFFSET = 20;
const GRID_SIZE = 30;

// Helpers (grid/safe zone) are flagged this way to exclude them from exports,
// selection and the context menu (they are also evented:false, so Fabric already ignores them on hit-testing).
type HelperObject = FabricObject & { isGridLine?: boolean; isSafeZone?: boolean };

// Main editor area: dotted background + 1:1 artboard.
// Zoom visually scales the whole canvas via CSS transform,
// not Fabric's internal zoom, which only rescales content within the same pixel size.
const CanvasStage = () => {
  const wrapperElRef = useRef<HTMLDivElement>(null);
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const clipboardRef = useRef<FabricObject | null>(null);
  const {
    canvas,
    registerCanvas,
    addImageFromUrl,
    zoom,
    setZoomLevel,
    registerZoomToFit,
    gridEnabled,
    setGridEnabled,
    safeZoneEnabled,
    setSafeZoneEnabled,
    safeZoneMarginPercent,
    snapToGridEnabled,
    setSnapToGridEnabled,
    backgroundColor,
    canvasWidth,
    canvasHeight,
    setSelectedObject,
    recordHistory,
    setActiveRailTab,
  } = useCanvasEditor();

  const [menu, setMenu] = useState<{ x: number; y: number; target: FabricObject | null } | null>(null);

  // Ref to read the latest toggle value inside a listener
  // that is registered only once when the canvas is created.
  const snapToGridRef = useRef(snapToGridEnabled);
  useEffect(() => {
    snapToGridRef.current = snapToGridEnabled;
  }, [snapToGridEnabled]);

  // Same pattern: the fabric listener is registered only once, so it reads the latest
  // recordHistory via ref instead of depending on it (which would recreate the canvas).
  const recordHistoryRef = useRef(recordHistory);
  useEffect(() => {
    recordHistoryRef.current = recordHistory;
  }, [recordHistory]);

  useEffect(() => {
    if (!canvasElRef.current) return;

    const fabricCanvas = new Canvas(canvasElRef.current, {
      width: canvasWidth,
      height: canvasHeight,
      backgroundColor,
      preserveObjectStacking: true,
      selection: true,
    });

    // Right click: selects the object (if any) and opens the corresponding context menu.
    // The native 'contextmenu' event is used (not fabric's 'mouse:down' + button check) because
    // it is more reliable across browsers for detecting a right click.
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      const { target } = fabricCanvas.findTarget(e);

      if (target) {
        fabricCanvas.setActiveObject(target);
        fabricCanvas.requestRenderAll();
      }
      setMenu({ x: e.clientX, y: e.clientY, target: target ?? null });
    };
    fabricCanvas.upperCanvasEl.addEventListener('contextmenu', handleContextMenu);

    // If the object has an animation running, it stops as soon as it's grabbed to drag/
    // scale/rotate; otherwise the animation kept writing left/top every frame and fought
    // with fabric's drag (the object couldn't be moved with the infinite loop enabled).
    fabricCanvas.on('mouse:down', (opt) => {
      const target = opt.target as AnimatableObject | undefined;
      if (target) stopObjectAnimation(target);
    });

    // Snap to the grid when moving an object.
    // The absolute bounding box is used (not left/top) because the objects we add
    // (images, text) use origin 'center': snapping left/top directly would align
    // the CENTER to the grid, not the visible edge, misaligning the object from the grid
    // by an amount that depends on its size.
    fabricCanvas.on('object:moving', (opt) => {
      const target = opt.target as HelperObject;
      if (!target || target.isGridLine || target.isSafeZone || !snapToGridRef.current) return;

      // Fabric updates left/top before emitting 'moving' but doesn't refresh aCoords,
      // so getBoundingRect() would return the previous frame's bounding box without this
      // (causing oscillation while dragging instead of a stable snap).
      target.setCoords();
      const bounds = target.getBoundingRect();
      const snappedLeft = Math.round(bounds.left / GRID_SIZE) * GRID_SIZE;
      const snappedTop = Math.round(bounds.top / GRID_SIZE) * GRID_SIZE;

      target.set({
        left: (target.left ?? 0) + (snappedLeft - bounds.left),
        top: (target.top ?? 0) + (snappedTop - bounds.top),
      });
    });

    // Tracks the active selection to show/hide the floating edit bar.
    // Selecting an object plays its configured animation; deselecting it
    // (click on another object or on empty space) stops its own.
    fabricCanvas.on('selection:created', (opt) => {
      // Replacing the selection fires 'selection:updated', which takes over from here.
      if (enforceSelectionLocks(fabricCanvas)) return;
      const target = (opt.selected?.[0] ?? null) as AnimatableObject | null;
      setSelectedObject(target);
      if (target?.animationConfig?.type) playObjectAnimation(fabricCanvas, target);
    });
    fabricCanvas.on('selection:updated', (opt) => {
      if (enforceSelectionLocks(fabricCanvas)) return;
      (opt.deselected as AnimatableObject[]).forEach((object) => stopObjectAnimation(object));
      const target = (opt.selected?.[0] ?? null) as AnimatableObject | null;
      setSelectedObject(target);
      if (target?.animationConfig?.type) playObjectAnimation(fabricCanvas, target);
    });
    fabricCanvas.on('selection:cleared', (opt) => {
      (opt.deselected as AnimatableObject[]).forEach((object) => stopObjectAnimation(object));
      setSelectedObject(null);
    });

    // Records history for what fabric already detects on its own: adding/removing objects and the
    // end of a drag/scale/rotate ('object:modified' fires once per gesture,
    // not every frame, so no debounce is needed here).
    fabricCanvas.on('object:added', (opt) => {
      const target = opt.target as HelperObject;
      if (target.isGridLine || target.isSafeZone) return;
      keepGuidesOnTop(fabricCanvas);
      recordHistoryRef.current('Agregar elemento');
    });
    fabricCanvas.on('object:removed', (opt) => {
      const target = opt.target as HelperObject;
      if (target.isGridLine || target.isSafeZone) return;
      recordHistoryRef.current('Eliminar elemento');
    });
    fabricCanvas.on('object:modified', () => recordHistoryRef.current('Mover/transformar elemento'));

    registerCanvas(fabricCanvas);

    return () => {
      fabricCanvas.upperCanvasEl.removeEventListener('contextmenu', handleContextMenu);
      registerCanvas(null);
      void fabricCanvas.dispose();
    };
  }, [registerCanvas]);

  // Applies the chosen canvas size to the artboard.
  useEffect(() => {
    // canvas.disposed avoids operating on an already destroyed instance: under HMR/Fast Refresh
    // this effect can fire with the old canvas right after its cleanup disposed it.
    if (!canvas || canvas.disposed) return;
    canvas.setDimensions({ width: canvasWidth, height: canvasHeight });
    setZoomLevel(1); // Reset zoom when the format changes.
    canvas.requestRenderAll();
  }, [canvas, canvasWidth, canvasHeight, setZoomLevel]);

  // Applies the chosen background color to the artboard.
  useEffect(() => {
    if (!canvas || canvas.disposed) return;
    canvas.backgroundColor = backgroundColor;
    canvas.requestRenderAll();
  }, [canvas, backgroundColor]);

  // Draws/removes the grid lines.
  useEffect(() => {
    if (!canvas || canvas.disposed) return;

    (canvas.getObjects() as HelperObject[]).filter((obj) => obj.isGridLine).forEach((obj) => canvas.remove(obj));

    if (gridEnabled) {
      for (let x = 0; x <= canvasWidth; x += GRID_SIZE) {
        const line: HelperObject = new Polyline([{ x, y: 0 }, { x, y: canvasHeight }], {
          stroke: '#e0e0e0',
          strokeWidth: 1,
          selectable: false,
          evented: false,
        });
        line.isGridLine = true;
        canvas.add(line);
      }
      for (let y = 0; y <= canvasHeight; y += GRID_SIZE) {
        const line: HelperObject = new Polyline([{ x: 0, y }, { x: canvasWidth, y }], {
          stroke: '#e0e0e0',
          strokeWidth: 1,
          selectable: false,
          evented: false,
        });
        line.isGridLine = true;
        canvas.add(line);
      }
      // Above the elements, but below the safe zone if it's already drawn.
      keepGuidesOnTop(canvas);
    }

    canvas.requestRenderAll();
  }, [canvas, gridEnabled, canvasWidth, canvasHeight]);

  // Draws/removes the safe zone perimeter (4 lines, same pattern as the grid)
  // instead of a Rect with a dashed stroke, which rendered incompletely.
  useEffect(() => {
    if (!canvas || canvas.disposed) return;

    (canvas.getObjects() as HelperObject[]).filter((obj) => obj.isSafeZone).forEach((obj) => canvas.remove(obj));

    if (safeZoneEnabled) {
      // The margin is proportional to the canvas size (not a fixed px value)
      // so the safe zone looks the same regardless of the chosen size/resolution.
      // safeZoneMarginPercent is the TOTAL crop (both sides combined), so it is
      // split in half per edge: e.g. 20% -> 10% margin per side.
      const minX = canvasWidth * (safeZoneMarginPercent / 2 / 100);
      const minY = canvasHeight * (safeZoneMarginPercent / 2 / 100);
      const maxX = canvasWidth - minX;
      const maxY = canvasHeight - minY;
      const perimeter: [number, number, number, number][] = [
        [minX, minY, maxX, minY], // top
        [maxX, minY, maxX, maxY], // right
        [maxX, maxY, minX, maxY], // bottom
        [minX, maxY, minX, minY], // left
      ];

      perimeter.forEach(([x1, y1, x2, y2]) => {
        const line: HelperObject = new Polyline([{ x: x1, y: y1 }, { x: x2, y: y2 }], {
          stroke: '#FF6B6B',
          strokeWidth: 2,
          strokeDashArray: [10, 5],
          selectable: false,
          evented: false,
        });
        line.isSafeZone = true;
        canvas.add(line);
      });
    }

    canvas.requestRenderAll();
  }, [canvas, safeZoneEnabled, safeZoneMarginPercent, canvasWidth, canvasHeight]);


  useEffect(() => {
    registerZoomToFit(() => {
      const wrapperEl = wrapperElRef.current;
      if (!wrapperEl) return;

      const wrapperWidth = wrapperEl.clientWidth - FIT_PADDING;
      const wrapperHeight = wrapperEl.clientHeight - FIT_PADDING;
      const scale = Math.min(wrapperWidth / canvasWidth, wrapperHeight / canvasHeight, 1);
      setZoomLevel(scale);
    });

    return () => registerZoomToFit(null);
  }, [registerZoomToFit, setZoomLevel, canvasWidth, canvasHeight]);

  // Zoom with Ctrl/Cmd + mouse wheel.
  // A native listener is used (not React's onWheel) because React registers it
  // as passive, which prevents calling preventDefault inside the handler.
  const zoomRef = useRef(zoom);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    const wrapperEl = wrapperElRef.current;
    if (!wrapperEl) return;

    const handleWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      setZoomLevel(zoomRef.current + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
    };

    wrapperEl.addEventListener('wheel', handleWheel, { passive: false });
    return () => wrapperEl.removeEventListener('wheel', handleWheel);
  }, [setZoomLevel]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const url = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
    if (!url) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    void addImageFromUrl(
      url,
      { x, y },
      e.dataTransfer.getData(LAYER_NAME_DRAG_TYPE) || undefined,
      parseLayerSourceRef(e.dataTransfer.getData(LAYER_SOURCE_DRAG_TYPE))
    );
  };

  // Copies keep the custom props (source, animation, name...). The template can't be copied: a canvas has only one.
  const handleCopy = async (target: FabricObject) => {
    if (isTemplateLayer(target)) return;
    clipboardRef.current = await target.clone(CANVAS_CUSTOM_PROPS);
  };

  const handlePaste = async () => {
    if (!canvas || !clipboardRef.current) return;
    const clone = await clipboardRef.current.clone(CANVAS_CUSTOM_PROPS);
    clone.set({ left: (clone.left ?? 0) + CLONE_OFFSET, top: (clone.top ?? 0) + CLONE_OFFSET });
    canvas.add(clone);
    canvas.setActiveObject(clone);
    canvas.requestRenderAll();
  };

  const handleDuplicate = async (target: FabricObject) => {
    if (!canvas || isTemplateLayer(target)) return;
    const clone = await target.clone(CANVAS_CUSTOM_PROPS);
    clone.set({ left: (target.left ?? 0) + CLONE_OFFSET, top: (target.top ?? 0) + CLONE_OFFSET });
    canvas.add(clone);
    canvas.setActiveObject(clone);
    canvas.requestRenderAll();
  };

  const handleDelete = (target: FabricObject) => {
    if (!canvas) return;
    canvas.remove(target);
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  };

  const getLayerSubmenuItems = (target: FabricObject): ContextMenuItem[] => {
    const move = (direction: 'front' | 'forward' | 'backward' | 'back', label: string) => {
      if (!canvas) return;
      moveLayer(canvas, target, direction);
      recordHistory(`${label} "${getLayerName(target)}"`);
    };

    return [
      { id: 'layers-front', label: 'Pasar al frente', icon: 'arrow/chevron-double-up', onSelect: () => move('front', 'Pasar al frente') },
      { id: 'layers-forward', label: 'Mover hacia adelante', icon: 'arrow/arrow-up', onSelect: () => move('forward', 'Mover hacia adelante') },
      { id: 'layers-backward', label: 'Mover hacia atrás', icon: 'arrow/arrow-down', onSelect: () => move('backward', 'Mover hacia atrás') },
      { id: 'layers-back', label: 'Mover al fondo', icon: 'arrow/chevron-double-down', onSelect: () => move('back', 'Mover al fondo') },
      { id: 'layers-show', label: 'Mostrar capas', icon: 'functionalities/consolidated', divider: true, onSelect: () => setActiveRailTab('capas') },
    ];
  };

  const getAlignSubmenuItems = (target: FabricObject): ContextMenuItem[] => {
    const align = (alignment: 'left' | 'center-h' | 'right' | 'top' | 'center-v' | 'bottom', label: string) => {
      if (!canvas) return;
      alignLayer(canvas, target, alignment);
      recordHistory(`${label} "${getLayerName(target)}"`);
    };

    return [
      { id: 'align-left', label: 'Izquierda', icon: 'arrow/arrow-left', onSelect: () => align('left', 'Alinear a la izquierda') },
      { id: 'align-center-h', label: 'Centrar horizontal', icon: 'arrow/flip-horizontal', onSelect: () => align('center-h', 'Centrar horizontalmente') },
      { id: 'align-right', label: 'Derecha', icon: 'arrow/arrow-right', onSelect: () => align('right', 'Alinear a la derecha') },
      { id: 'align-top', label: 'Arriba', icon: 'arrow/arrow-up', divider: true, onSelect: () => align('top', 'Alinear arriba') },
      { id: 'align-center-v', label: 'Centrar vertical', icon: 'arrow/flip-vertical', onSelect: () => align('center-v', 'Centrar verticalmente') },
      { id: 'align-bottom', label: 'Abajo', icon: 'arrow/arrow-down', onSelect: () => align('bottom', 'Alinear abajo') },
    ];
  };

  const getCanvasMenuItems = (): ContextMenuItem[] => [
    { id: 'paste', label: 'Pegar', icon: 'functionalities/par', hint: 'Ctrl+V', onSelect: () => void handlePaste() },
    { id: 'safe-zone', label: 'Zona segura', icon: 'media/maximize', divider: true, checked: safeZoneEnabled, keepOpen: true, onSelect: () => setSafeZoneEnabled(!safeZoneEnabled) },
    { id: 'grid', label: 'Cuadrícula', icon: 'layout/grid', checked: gridEnabled, keepOpen: true, onSelect: () => setGridEnabled(!gridEnabled) },
    { id: 'snap', label: 'Ajustar a guías', icon: 'functionalities/unlink', checked: snapToGridEnabled, keepOpen: true, onSelect: () => setSnapToGridEnabled(!snapToGridEnabled) },
  ];

  const getCopyMenuItems = (target: FabricObject): ContextMenuItem[] =>
    isTemplateLayer(target)
      ? [{ id: 'paste', label: 'Pegar', icon: 'functionalities/par', hint: 'Ctrl+V', onSelect: () => void handlePaste() }]
      : [
          { id: 'copy', label: 'Copiar', icon: 'functionalities/copy', hint: 'Ctrl+C', onSelect: () => void handleCopy(target) },
          { id: 'paste', label: 'Pegar', icon: 'functionalities/par', hint: 'Ctrl+V', onSelect: () => void handlePaste() },
          { id: 'duplicate', label: 'Duplicar', icon: 'functionalities/copy', hint: 'Ctrl+D', onSelect: () => void handleDuplicate(target) },
        ];

  const getImageMenuItems = (target: FabricObject): ContextMenuItem[] => [
    ...getCopyMenuItems(target),
    { id: 'delete', label: 'Eliminar', icon: 'system/trash', hint: 'Delete', danger: true, onSelect: () => handleDelete(target) },
    {
      id: 'block',
      label: isTargetLocked(target) ? 'Desbloquear' : 'Bloquear',
      icon: isTargetLocked(target) ? 'functionalities/unlock' : 'functionalities/lock',
      keepOpen: true,
      onSelect: () => {
        const nextLocked = !isTargetLocked(target);
        setTargetLocked(target, nextLocked);
        // The handles show/hide right away.
        canvas?.requestRenderAll();
        recordHistory(nextLocked ? 'Bloquear elemento' : 'Desbloquear elemento');
      },
    },
    {
      id: 'layers',
      label: 'Capas',
      icon: 'arrow/chevron-right',
      iconPosition: 'right',
      divider: true,
      submenu: getLayerSubmenuItems(target),
      onSelect: () => { },
    },
    // Aligning moves the element, so a locked one doesn't offer it.
    ...(isTargetLocked(target)
      ? []
      : [{ id: 'align', label: 'Alinear', icon: 'arrow/chevron-right', iconPosition: 'right', submenu: getAlignSubmenuItems(target), onSelect: () => { } } as ContextMenuItem]),
    { id: 'safe-zone', label: 'Zona segura', icon: 'media/maximize', divider: true, checked: safeZoneEnabled, keepOpen: true, onSelect: () => setSafeZoneEnabled(!safeZoneEnabled) },
    { id: 'grid', label: 'Cuadrícula', icon: 'layout/grid', checked: gridEnabled, keepOpen: true, onSelect: () => setGridEnabled(!gridEnabled) },
    { id: 'snap', label: 'Ajustar a guías', icon: 'functionalities/unlink', checked: snapToGridEnabled, keepOpen: true, onSelect: () => setSnapToGridEnabled(!snapToGridEnabled) },
  ];

  return (
    <>
      {/* The toolbar sits outside the scrolling area so it stays in view while the canvas scrolls. */}
      <div className="relative flex-1 min-w-0 flex">
        <CanvasFloatingToolbar />

        <div
          ref={wrapperElRef}
          onContextMenu={(e) => e.preventDefault()}
          className="flex-1 min-w-0 overflow-auto p-4 flex justify-center-safe items-center-safe bg-gray-50 bg-[radial-gradient(circle,#d4d4d4_1px,transparent_1px)] bg-size-[16px_16px]"
        >
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
            className="shrink-0 bg-white shadow-lg transition-transform duration-100 ease-out"
          >
            <canvas ref={canvasElRef} />
          </div>
        </div>
      </div>

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={menu.target ? getImageMenuItems(menu.target) : getCanvasMenuItems()}
          onClose={() => setMenu(null)}
        />
      )}
    </>
  );
};

export default CanvasStage;


